/**
 * ReenterApiKeyModal — paste-and-validate Dialog for auth_failed credentials.
 *
 * Opened from the blocked credential row in CredentialPickerExistingBlock when
 * blockedReason.reason === 'auth_failed'. The user pastes a new API key, which
 * the modal validates immediately via PUT /v1/credentials/:credentialId.
 *
 * On success:
 *   1. Close modal.
 *   2. Re-fetch available credentials (prefetchAvailableCredentialsForApp) so
 *      the Redux picker list picks up the cleared blockedReason — the row
 *      converts to a normal selectable pill without page reload.
 *   3. Announce via role="alert".
 *
 * On failure:
 *   - auth_failed: "This key is not valid. Check that you copied the correct key."
 *   - access_denied: "This key is still missing required scopes. Make sure you
 *     selected all available scopes when creating the key."
 *   - generic: "Failed to save. Please try again."
 *
 * Brand guide §37e v2 (2026-05-26). Phase E Slice 2.4 — TDD §11.
 *
 * Architecture: standard React hooks (useState) only — no react-hook-form needed
 * for a single paste field with inline error.
 */

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loader2, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { CredentialsAPI } from '@/API/Credentials'
import { prefetchAvailableCredentialsForApp } from '@/services/DashboardChatService'
import type { AppDispatch, RootState } from '@/redux'

interface ReenterApiKeyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  credentialId: string
  credentialLabel: string
  /** Source app code (e.g. 'campminder') for post-success prefetch. */
  sourceAppCode: string
  /** Callback fired when the update succeeds and the row should animate clear. */
  onSuccess: (credentialLabel: string) => void
}

type ValidationError =
  | 'auth_failed'
  | 'access_denied'
  | 'generic'

function validationErrorMessage(error: ValidationError): string {
  switch (error) {
    case 'auth_failed':
      return 'This key is not valid. Check that you copied the correct key.'
    case 'access_denied':
      return 'This key is still missing required scopes. Make sure you selected all available scopes when creating the key.'
    case 'generic':
      return 'Failed to save. Please try again.'
  }
}

export function ReenterApiKeyModal({
  open,
  onOpenChange,
  credentialId,
  credentialLabel,
  sourceAppCode,
  onSuccess,
}: ReenterApiKeyModalProps) {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((s: RootState) => s.user.token)

  const [newApiKey, setNewApiKey] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<ValidationError | null>(null)

  const handleClose = () => {
    // Reset state on close so the modal is fresh if reopened.
    setNewApiKey('')
    setValidationError(null)
    setIsValidating(false)
    onOpenChange(false)
  }

  const handleSaveAndValidate = async () => {
    if (!token || !newApiKey.trim()) return
    setIsValidating(true)
    setValidationError(null)

    try {
      // Step 1: fetch credential details to get loginMethodId + nickname.
      // Required by the PUT body (CredentialUpdateRequestBody).
      // Use the API module directly (not the credentialsService thunk) to avoid
      // toggling the global app.loading flag, which would block the entire creation
      // UI with a full-screen overlay while this local validate flow is in progress.
      const detailsResp = await CredentialsAPI.getCredentialDetails(token, credentialId)
      if (!detailsResp.data) {
        setValidationError('generic')
        setIsValidating(false)
        return
      }

      const { loginMethodId, nickname } = detailsResp.data

      // Step 2: PUT updated credential with the new API key.
      // credentialAttributeCode for CampMinder API key is 'api_key' (v1 constraint).
      const updateResp = await CredentialsAPI.update(token, credentialId, {
        appId: detailsResp.data.app.id,
        loginMethodId,
        nickname,
        credentialValues: [
          { credentialAttributeCode: 'api_key', attributeValue: newApiKey.trim() },
        ],
      })

      if (!updateResp.data) {
        // Map the BE error message to a validation error type.
        // The BE returns the blockedReason in the error message for validation failures.
        const errMsg = updateResp.err ?? ''
        if (errMsg.toLowerCase().includes('access_denied') || errMsg.toLowerCase().includes('scope')) {
          setValidationError('access_denied')
        } else if (errMsg.toLowerCase().includes('auth_failed') || errMsg.toLowerCase().includes('invalid')) {
          setValidationError('auth_failed')
        } else {
          setValidationError('generic')
        }
        setIsValidating(false)
        return
      }

      // Step 3: Re-fetch availableCredentials so the picker row updates in-place.
      // BE update hook invalidates cache + re-probes, so the new availabilitySummary
      // is already reflected in GET /v1/credentials by the time we refetch.
      await dispatch(prefetchAvailableCredentialsForApp(token, sourceAppCode))

      setIsValidating(false)
      handleClose()
      onSuccess(credentialLabel)
    } catch {
      setValidationError('generic')
      setIsValidating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={/* istanbul ignore next */ (nextOpen) => { if (!nextOpen) handleClose() }}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Re-enter API key</DialogTitle>
          <DialogDescription>
            Paste your new {credentialLabel} API key below. It will be validated immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-1">
          <Label htmlFor="reenter-api-key-input">
            New API key
          </Label>
          <Input
            id="reenter-api-key-input"
            type="password"
            placeholder="Paste your new API key here"
            aria-label={`New API key for ${credentialLabel}`}
            value={newApiKey}
            onChange={e => {
              setNewApiKey(e.target.value)
              // Clear inline error on new input so the user isn't stuck on a stale error.
              if (validationError) setValidationError(null)
            }}
            autoComplete="off"
            autoFocus
          />
          {validationError && (
            <p
              className="text-xs text-destructive mt-0.5"
              role="alert"
              aria-live="polite"
            >
              {validationErrorMessage(validationError)}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isValidating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSaveAndValidate()}
            disabled={!newApiKey.trim() || isValidating}
            aria-busy={isValidating}
          >
            {isValidating
              ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" />
                  Validating&hellip;
                </>
              )
              : (
                <>
                  <Check size={14} className="mr-2" aria-hidden="true" />
                  Save &amp; validate
                </>
              )
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
